// Centralized datetime formatting for the dashboard. Backend stores
// naive UTC (datetime.utcnow); JS parses naive ISO as local time, so
// every helper here forces UTC interpretation when no tz suffix is
// present, then renders in America/New_York with the TZ abbreviation
// (EST/EDT) appended.
//
// Use these helpers everywhere a backend timestamp is shown to a user
// — never call `new Date(x).toLocaleString()` directly. We default to
// ET because the firm operates from the US east coast and inconsistent
// per-browser local rendering creates real confusion (e.g. "next opens
// at" times that look 5h off in winter, 4h in summer).

const ET = 'America/New_York';

/**
 * Naive UTC iso strings (no Z, no offset) get a Z appended so JS
 * doesn't parse them as local. The regex matches Z, z, or `±HH:MM`
 * / `±HHMM` offsets at the end.
 *
 * EXPORTED (timeline-row-layout-spec §3) so the CRM tab's own formatters normalise the SAME way
 * rather than carrying a second copy of this rule — a row whose date and time disagreed by a
 * browser offset is exactly what a duplicate would produce. An unparseable string still yields an
 * Invalid Date; every caller checks.
 */
export function ensureUtc(ts: string): Date {
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(ts);
  return new Date(hasTz ? ts : ts + 'Z');
}

/**
 * Full-precision date+time in ET, e.g. "May 7, 2026, 9:00 AM EDT".
 * Use for primary timestamps where the full date matters (created at,
 * decision banners, snapshot times).
 */
export function formatEt(ts: string | null | undefined): string {
  if (!ts) return '';
  const d = ensureUtc(ts);
  const formatted = d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ET,
  });
  return `${formatted} ET`;
}

/**
 * Compact ET formatting for tables: "5/7 9:00 AM ET". Use when row
 * height matters and the year is implicit (recent activity, recipient
 * tables, tick events). Drops the year + uses numeric month/day.
 */
export function formatEtShort(ts: string | null | undefined): string {
  if (!ts) return '';
  const d = ensureUtc(ts);
  const formatted = d.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ET,
  });
  return `${formatted} ET`;
}

/**
 * Date-only in ET: "May 7, 2026". For audit-log style entries where
 * time-of-day is irrelevant.
 */
export function formatEtDate(ts: string | null | undefined): string {
  if (!ts) return '';
  const d = ensureUtc(ts);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: ET,
  });
}

/**
 * Pre-existing alias for callers that imported the old name. Same
 * semantics as `formatEt`. New code should use `formatEt`.
 */
export const formatSnapshotTimestamp = formatEt;
